// Accss design system v2 - shared building blocks (same visual language as the widget).
// Use these instead of ad-hoc sx so the marketing site and the dashboard stay consistent.
import React from "react";
import { alpha, Box, Card, Stack, Typography, useTheme } from "@mui/material";

const TONES = {
  primary: (t) => t.palette.primary.main,
  success: (t) => t.palette.success.main,
  warning: (t) => t.palette.warning.main,
  error: (t) => t.palette.error.main,
  info: (t) => t.palette.info.main,
  violet: () => "#7c3aed",
  teal: () => "#0f766e",
  neutral: (t) => t.palette.text.secondary,
};

export function toneColor(theme, tone = "primary") {
  return (TONES[tone] || TONES.primary)(theme);
}

/** Small rounded square holding an icon (like the widget's tool icons). */
export function IconTile({ children, tone = "primary", size = 40, variant = "soft", sx }) {
  const theme = useTheme();
  const c = toneColor(theme, tone);
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: size,
        height: size,
        flex: "none",
        borderRadius: size >= 44 ? "14px" : "12px",
        display: "grid",
        placeItems: "center",
        color: variant === "solid" ? "#fff" : c,
        bgcolor: variant === "solid" ? c : alpha(c, theme.palette.mode === "dark" ? 0.18 : 0.1),
        border: variant === "outline" ? `1px solid ${theme.aw.border}` : "none",
        "& svg, & .material-symbols-outlined": { fontSize: Math.round(size * 0.5) },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** Page title block used at the top of every dashboard page. */
export function PageHeader({ title, subtitle, actions, eyebrow, sx }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "stretch", sm: "flex-end" }}
      justifyContent="space-between"
      spacing={2}
      sx={{ mb: 3.5, ...sx }}
    >
      <Box sx={{ minWidth: 0 }}>
        {eyebrow ? (
          <Typography variant="overline" component="div" sx={{ mb: 0.5 }}>
            {eyebrow}
          </Typography>
        ) : null}
        <Typography variant="h3" component="h1" sx={{ fontSize: { xs: "1.6rem", md: "1.9rem" } }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{ color: "text.secondary", mt: 0.75, maxWidth: 720 }}>{subtitle}</Typography>
        ) : null}
      </Box>
      {actions ? (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ flex: "none" }}>
          {actions}
        </Stack>
      ) : null}
    </Stack>
  );
}

/** Bordered card with an optional header row (title / description / action). */
export function SectionCard({ title, description, icon, iconTone, action, children, sx, contentSx, ...rest }) {
  return (
    <Card sx={{ ...sx }} {...rest}>
      {title || action ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ px: 3, pt: 2.5, pb: description ? 0.5 : 0 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            {icon ? (
              <IconTile tone={iconTone} size={36}>
                {icon}
              </IconTile>
            ) : null}
            <Box sx={{ minWidth: 0 }}>
              {title ? (
                <Typography variant="h6" component="h2">
                  {title}
                </Typography>
              ) : null}
              {description ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {description}
                </Typography>
              ) : null}
            </Box>
          </Stack>
          {action ? <Box sx={{ flex: "none" }}>{action}</Box> : null}
        </Stack>
      ) : null}
      <Box sx={{ p: 3, pt: title || action ? 2 : 3, ...contentSx }}>{children}</Box>
    </Card>
  );
}

/** KPI tile: icon + label + big value + hint. */
export function StatTile({ icon, tone = "primary", label, value, hint, sx }) {
  return (
    <Card sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5, ...sx }}>
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <IconTile tone={tone} size={36}>
          {icon}
        </IconTile>
        <Typography variant="subtitle2" component="div">
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: "1.9rem", fontWeight: 800, lineHeight: 1, letterSpacing: "-0.02em" }}>{value}</Typography>
      {hint ? (
        <Typography variant="caption" component="div">
          {hint}
        </Typography>
      ) : null}
    </Card>
  );
}

/** Pill-shaped small label (status, badge, eyebrow). */
export function Pill({ children, tone = "primary", sx }) {
  const theme = useTheme();
  const c = toneColor(theme, tone);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.4,
        borderRadius: 999,
        fontSize: "0.75rem",
        fontWeight: 700,
        color: c,
        bgcolor: alpha(c, theme.palette.mode === "dark" ? 0.2 : 0.1),
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** Empty state block for lists with no data. */
export function EmptyState({ icon, title, description, action, sx }) {
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ textAlign: "center", py: 6, px: 2, ...sx }}>
      {icon ? (
        <IconTile size={56} tone="neutral">
          {icon}
        </IconTile>
      ) : null}
      <Typography variant="h6">{title}</Typography>
      {description ? (
        <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 420 }}>
          {description}
        </Typography>
      ) : null}
      {action}
    </Stack>
  );
}
